import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal, WritableSignal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { switchMap, tap } from 'rxjs';
import {
  BottomNavbarComponent,
  GameCardV2Component,
  PageLayoutComponent,
  BadgeModeEnum,
  RedirectTypeEnum,
  TitileNavbarComponent
} from '@ui-components';
import { AppRoutes, FindGamesRoutes, HostedGamesRoutes } from '@core';
import { CreateGameViewService } from '@hosted-games';
import { GetMyGamesQueryHandler } from '../../application/handlers';
import { MyGameItem, MyGamesSource } from '../../application/models';
import { MyGamesSegmentComponent } from '../my-games-segment/my-games-segment.component';

interface EmptyStateCta {
  readonly label: string;
  readonly action: () => void;
}

@Component({
  selector: 'lib-my-games',
  templateUrl: './my-games.component.html',
  styleUrls: ['./my-games.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TitileNavbarComponent,
    BottomNavbarComponent,
    PageLayoutComponent,
    GameCardV2Component,
    MyGamesSegmentComponent
  ],
  providers: [GetMyGamesQueryHandler]
})
export class MyGamesComponent {
  private static readonly SOURCE_QUERY_PARAM: string = 'source';

  private readonly getMyGamesQueryHandler: GetMyGamesQueryHandler = inject(GetMyGamesQueryHandler);
  private readonly createGameViewService: CreateGameViewService = inject(CreateGameViewService);
  private readonly router: Router = inject(Router);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);

  protected readonly RedirectTypeEnum: typeof RedirectTypeEnum = RedirectTypeEnum;
  protected readonly badgeMode: typeof BadgeModeEnum = BadgeModeEnum;

  private readonly isPageLoaded: WritableSignal<boolean> = signal<boolean>(false);

  readonly source: WritableSignal<MyGamesSource> = signal<MyGamesSource>(this.readSourceFromUrl());

  readonly grouped: Signal<Map<string, MyGameItem[]> | undefined> = toSignal(
    toObservable(this.source).pipe(
      switchMap((source: MyGamesSource) => this.getMyGamesQueryHandler.getGroupedByMonth(source)),
      tap(() => this.isPageLoaded.set(true))
    )
  );

  readonly months: Signal<string[]> = computed<string[]>(() => {
    const map: Map<string, MyGameItem[]> | undefined = this.grouped();
    return map ? Array.from(map.keys()) : [];
  });

  readonly showPlaceholder: Signal<boolean> = computed<boolean>(() => {
    const map: Map<string, MyGameItem[]> | undefined = this.grouped();
    return this.isPageLoaded() && (!map || map.size === 0);
  });

  readonly emptyStateCta: Signal<EmptyStateCta> = computed<EmptyStateCta>(() => {
    if (this.source() === MyGamesSource.HOSTED) {
      return { label: 'Dodaj gierkę', action: (): void => this.goToCreateGame() };
    }
    return { label: 'Znajdź gierkę', action: (): void => this.goToFindGames() };
  });

  changeSource(source: MyGamesSource): void {
    this.source.set(source);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [MyGamesComponent.SOURCE_QUERY_PARAM]: source },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  getGamesForMonth(month: string): MyGameItem[] {
    return this.grouped()?.get(month) ?? [];
  }

  addSimilarGame(game: MyGameItem): void {
    this.createGameViewService.setGame(game);
    this.goToCreateGame();
  }

  private goToCreateGame(): void {
    this.router.navigateByUrl(`${AppRoutes.HOST}/${HostedGamesRoutes.CREATE_GAME}`);
  }

  private goToFindGames(): void {
    this.router.navigateByUrl(`${AppRoutes.FIND_GAMES}/${FindGamesRoutes.MAP}`);
  }

  private readSourceFromUrl(): MyGamesSource {
    const param: string | null = this.route.snapshot.queryParamMap.get(MyGamesComponent.SOURCE_QUERY_PARAM);
    return Object.values(MyGamesSource).includes(param as MyGamesSource) ? (param as MyGamesSource) : MyGamesSource.ALL;
  }
}
